import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useSendMessageStore } from '@lib/store';
import { cn } from '@shared/utils';
import { Button, Input, Textarea } from '@shared/widgets';
import CloseIcon from '@assets/icons/close.svg';

interface IForm {
  subject: string;
  body: string;
}
const DEFAULT_VALUES: IForm = { subject: '', body: '' };

interface NewMessageProps {
  className?: string;
  isShown: boolean;
  onClose: () => void;
}

export const NewMessage = (props: NewMessageProps) => {
  const params = useParams();

  const { loading, sendMessage, clear } = useSendMessageStore();

  const [animation, setAnimation] = useState<boolean>(false);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    setAnimation(props.isShown);
  }, [props.isShown]);
  const transitionEnd = () => !props.isShown && setAnimation(false);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
    clear();
  };

  const submitHandler = async (data: IForm) => {
    const result = await sendMessage({ Subject: data.subject, Body: data.body }, params.dir === 'sent');
    if (result.data) closeHandler();
  };

  return (
    <div
      className={cn(
        'fixed bottom-[80px] left-1/2 z-[1500] ml-[177px] w-[450px] rounded-sm shadow-[12px_12px_24px_0_#00000080] max-xl:right-[20px] max-xl:left-auto max-xl:ml-0 max-md:top-0 max-md:right-0 max-md:bottom-0 max-md:left-0 max-md:h-full max-md:w-full',
        'new-message-modal',
        animation
          ? 'visible scale-100 opacity-100 transition-[transform_400ms_ease,opacity_300ms_ease,visibility_0s_linear_0s]'
          : 'invisible scale-95 opacity-0 transition-[transform_400ms_ease,opacity_300ms_ease,visibility_0s_linear_400ms]',
        !props.isShown && 'hidden',
        props.className,
      )}
      onTransitionEnd={transitionEnd}
    >
      <div className='absolute top-[-5px] left-0 flex h-[67px] w-full items-center rounded-t-sm bg-[#1f1f1f] pr-14 pl-6 font-medium text-[#929292] uppercase'>
        New Message
        <CloseIcon className='absolute top-1/2 right-[18px] -translate-y-1/2 cursor-pointer' onClick={closeHandler} />
      </div>
      <form className='rounded-sm bg-[#fff] pt-29.5 pb-18 max-md:h-full max-md:rounded-none' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='subject'
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              {...field}
              className={cn(
                'absolute top-[67px] left-0 h-[51px] border-b !border-b-[#efefef] px-6 text-[#343434]',
                'placeholder:text-[#626262] placeholder:uppercase',
              )}
              placeholder='Title'
            />
          )}
        />
        <Controller
          name='body'
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Textarea
              {...field}
              className={cn('border-none px-6 py-4 text-[#343434]', 'placeholder:text-[#626262] placeholder:uppercase', 'max-md:h-full')}
              placeholder='Enter new message'
            />
          )}
        />
        <div className='absolute bottom-0 left-0 flex w-full justify-end p-4'>
          <Button className='h-[40px] px-5' type='submit' disabled={!formState.isValid || loading}>
            Send Message
          </Button>
        </div>
      </form>
    </div>
  );
};
